import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  TrendingUp,
  Calendar,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  Search,
  RefreshCw,
  Ticket,
  ShoppingCart,
  UserPlus,
  XCircle,
  Repeat
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function SalesTracking() {
  const { getAuthHeader } = useAuth();
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/sales-tracking`, getAuthHeader());
      setTrackingData(response.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleRow = (email) => {
    setExpandedRows(prev => ({
      ...prev,
      [email]: !prev[email]
    }));
  };

  const filteredData = trackingData.filter(person => 
    person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate grand totals
  const grandTotals = filteredData.reduce((acc, person) => ({
    ticket_count: acc.ticket_count + person.totals.ticket_count,
    sales_count: acc.sales_count + person.totals.sales_count,
    reference_count: acc.reference_count + person.totals.reference_count,
    cancelled_count: acc.cancelled_count + person.totals.cancelled_count,
    rpt_count: acc.rpt_count + person.totals.rpt_count,
    elsa_count: acc.elsa_count + person.totals.elsa_count
  }), { ticket_count: 0, sales_count: 0, reference_count: 0, cancelled_count: 0, rpt_count: 0, elsa_count: 0 });

  const formatMonthName = (monthKey) => {
    if (monthKey === 'Bilinmeyen') return monthKey;
    try {
      const [year, month] = monthKey.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
    } catch {
      return monthKey;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Satış Takip</h1>
          <p className="text-muted-foreground">Personel bazlı aylık satış istatistikleri</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </motion.div>

      {/* Summary Cards */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/30 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Ticket className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Toplam Bilet</span>
            </div>
            <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">{grandTotals.ticket_count.toLocaleString('tr-TR')}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/50 dark:to-green-900/30 border-green-200 dark:border-green-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingCart className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-green-700 dark:text-green-300">Toplam Satış</span>
            </div>
            <p className="text-2xl font-bold text-green-800 dark:text-green-200">{grandTotals.sales_count.toLocaleString('tr-TR')}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/50 dark:to-purple-900/30 border-purple-200 dark:border-purple-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <UserPlus className="h-4 w-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Toplam Referans</span>
            </div>
            <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">{grandTotals.reference_count.toLocaleString('tr-TR')}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/50 dark:to-emerald-900/30 border-emerald-200 dark:border-emerald-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Toplam ELSA</span>
            </div>
            <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">{grandTotals.elsa_count.toLocaleString('tr-TR')}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/50 dark:to-amber-900/30 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Repeat className="h-4 w-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Toplam RPT</span>
            </div>
            <p className="text-2xl font-bold text-amber-800 dark:text-amber-200">{grandTotals.rpt_count.toLocaleString('tr-TR')}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950/50 dark:to-red-900/30 border-red-200 dark:border-red-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="h-4 w-4 text-red-600" />
              <span className="text-xs font-medium text-red-700 dark:text-red-300">Toplam İptal</span>
            </div>
            <p className="text-2xl font-bold text-red-800 dark:text-red-200">{grandTotals.cancelled_count.toLocaleString('tr-TR')}</p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Search */}
      <motion.div variants={itemVariants}>
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Personel ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </motion.div>

      {/* Personnel List */}
      <motion.div variants={itemVariants} className="space-y-3">
        {filteredData.map((person, index) => (
          <Card key={person.email} data-testid={`personnel-card-${index}`}>
            <Collapsible open={expandedRows[person.email]} onOpenChange={() => toggleRow(person.email)}>
              <CollapsibleTrigger asChild>
                <div className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {person.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <CardTitle className="text-base font-semibold">{person.name}</CardTitle>
                          <CardDescription className="text-xs">{person.email}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="hidden sm:flex gap-3 text-sm">
                          <Badge variant="secondary" className="gap-1">
                            <Ticket className="h-3 w-3" />
                            {person.totals.ticket_count}
                          </Badge>
                          <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                            <ShoppingCart className="h-3 w-3" />
                            {person.totals.sales_count}
                          </Badge>
                          <Badge variant="secondary" className="gap-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                            <UserPlus className="h-3 w-3" />
                            {person.totals.reference_count}
                          </Badge>
                          {person.totals.cancelled_count > 0 && (
                            <Badge variant="secondary" className="gap-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                              <XCircle className="h-3 w-3" />
                              {person.totals.cancelled_count}
                            </Badge>
                          )}
                        </div>
                        {expandedRows[person.email] ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </div>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <CardContent className="pt-0">
                  {/* Mobile Summary */}
                  <div className="sm:hidden flex flex-wrap gap-2 mb-4">
                    <Badge variant="secondary" className="gap-1">
                      <Ticket className="h-3 w-3" />
                      Bilet: {person.totals.ticket_count}
                    </Badge>
                    <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700">
                      Satış: {person.totals.sales_count}
                    </Badge>
                    <Badge variant="secondary" className="gap-1 bg-purple-100 text-purple-700">
                      Referans: {person.totals.reference_count}
                    </Badge>
                    <Badge variant="secondary" className="gap-1 bg-emerald-100 text-emerald-700">
                      ELSA: {person.totals.elsa_count}
                    </Badge>
                    <Badge variant="secondary" className="gap-1 bg-amber-100 text-amber-700">
                      RPT: {person.totals.rpt_count}
                    </Badge>
                    {person.totals.cancelled_count > 0 && (
                      <Badge variant="secondary" className="gap-1 bg-red-100 text-red-700">
                        İptal: {person.totals.cancelled_count}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Monthly Breakdown Table */}
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="font-semibold">Ay</TableHead>
                          <TableHead className="text-center font-semibold">Bilet</TableHead>
                          <TableHead className="text-center font-semibold">Satış</TableHead>
                          <TableHead className="text-center font-semibold">Referans</TableHead>
                          <TableHead className="text-center font-semibold">ELSA</TableHead>
                          <TableHead className="text-center font-semibold">RPT</TableHead>
                          <TableHead className="text-center font-semibold">İptal</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(person.months).slice(0, 12).map(([monthKey, data]) => (
                          <TableRow key={monthKey}>
                            <TableCell className="font-medium">{formatMonthName(monthKey)}</TableCell>
                            <TableCell className="text-center">{data.ticket_count}</TableCell>
                            <TableCell className="text-center text-green-600 font-medium">{data.sales_count}</TableCell>
                            <TableCell className="text-center text-purple-600 font-medium">{data.reference_count}</TableCell>
                            <TableCell className="text-center text-emerald-600 font-medium">{data.elsa_count}</TableCell>
                            <TableCell className="text-center text-amber-600 font-medium">{data.rpt_count}</TableCell>
                            <TableCell className="text-center text-red-600 font-medium">{data.cancelled_count}</TableCell>
                          </TableRow>
                        ))}
                        {/* Totals Row */}
                        <TableRow className="bg-muted/30 font-bold border-t-2">
                          <TableCell className="font-bold">TOPLAM</TableCell>
                          <TableCell className="text-center font-bold">{person.totals.ticket_count}</TableCell>
                          <TableCell className="text-center text-green-700 font-bold">{person.totals.sales_count}</TableCell>
                          <TableCell className="text-center text-purple-700 font-bold">{person.totals.reference_count}</TableCell>
                          <TableCell className="text-center text-emerald-700 font-bold">{person.totals.elsa_count}</TableCell>
                          <TableCell className="text-center text-amber-700 font-bold">{person.totals.rpt_count}</TableCell>
                          <TableCell className="text-center text-red-700 font-bold">{person.totals.cancelled_count}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                  
                  {Object.keys(person.months).length > 12 && (
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Son 12 ay gösteriliyor. Toplam {Object.keys(person.months).length} ay verisi mevcut.
                    </p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ))}
        
        {filteredData.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Personel bulunamadı</p>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </motion.div>
  );
}
